import { useEffect, useMemo, useRef } from 'react';
import * as yup from 'yup';
import type { CreateTikTokAdParams } from '../api/createTikTokAd';
import useAdSetsData from './useAdSetsData';
import { useQueryClient } from '@tanstack/react-query';
import type { AdSettingEntry } from '../api';
import { queryKeys } from '../api';
import { createTikTokAdSchema } from '../validation/createTikTokAdSchema.schema';
import { useFormContext } from 'react-hook-form';
import { useTikTokCreatives } from 'features/creatives';

interface UseCreateTikTokAdParamsReturn {
  params: CreateTikTokAdParams;
  isValid: boolean;
  errors: string[];
  validationError: yup.ValidationError | null;
}

interface PackWithCreatives {
  tag: string;
  creatives: string[];
  filterCreativeByName?: string[];
}

const useCreateTikTokAdParams = (): UseCreateTikTokAdParamsReturn => {
  const { watch } = useFormContext();
  const app: string = watch('app');
  const accountId: string = watch('accountId');
  const packsWithCreatives: PackWithCreatives[] = watch('packsWithCreatives');

  const { selectedCampaignsWithAdSets, selectedAdSetsNames } = useAdSetsData();
  const queryClient = useQueryClient();
  const adSettings: AdSettingEntry[] | undefined = queryClient.getQueryData([
    queryKeys.accounts,
    app,
    selectedAdSetsNames,
  ]);

  const { rows: creativesList } = useTikTokCreatives();

  // Persist a cumulative map of creative id -> name just to avoid losing entries
  const creativesNameMapRef = useRef<Record<string, string>>({});

  useEffect(() => {
    if (!creativesList?.length) return;
    creativesList.forEach((c) => {
      if (c.id && c.name) {
        creativesNameMapRef.current[c.id] = c.name;
      }
    });
  }, [creativesList]);

  const params: CreateTikTokAdParams = useMemo(() => {
    return {
      app,
      ad_account_id: accountId ?? '',
      campaign_templates: selectedCampaignsWithAdSets ?? [],
      creative_packs: packsWithCreatives.map((p) => ({
        tag: p.tag,
        creative_names: p.creatives.map(
          (id) => creativesNameMapRef.current[id],
        ).filter((name): name is string => !!name),
      })),
      ad_settings: adSettings ?? [],
    };
  }, [
    app,
    accountId,
    selectedCampaignsWithAdSets,
    packsWithCreatives,
    adSettings,
    creativesNameMapRef,
  ]);

  const validation = useMemo(() => {
    try {
      createTikTokAdSchema.validateSync(params, { abortEarly: false });
      return {
        isValid: true,
        errors: [],
        validationError: null,
      };
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        return {
          isValid: false,
          errors: error.errors,
          validationError: error,
        };
      }
      return {
        isValid: false,
        errors: ['Unknown validation error occurred'],
        validationError: null,
      };
    }
  }, [params]);

  return {
    params,
    ...validation,
  };
};

export default useCreateTikTokAdParams;
